import React, { useLayoutEffect, useRef, useState } from 'react';
import * as S from './LayoutAlertPresence.styled';

type LayoutAlertPresenceProps = {
  open: boolean,
  children: React.ReactNode,
};

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

/**
 * Keeps a top alert mounted through its exit so height + fade can finish.
 * Starts collapsed, then opens on the next frame so CSS can play the enter.
 */
const LayoutAlertPresence = ({ open, children }: LayoutAlertPresenceProps) => {
  const [mounted, setMounted] = useState(open);
  const [expanded, setExpanded] = useState(false);
  const mountedRef = useRef(open);
  const lastChildrenRef = useRef(children);
  if (open) lastChildrenRef.current = children;

  useLayoutEffect(() => {
    const motionMs = prefersReducedMotion() ? 0 : S.LAYOUT_ALERT_MOTION_MS;
    if (open) {
      mountedRef.current = true;
      setMounted(true);
      if (motionMs === 0) {
        setExpanded(true);
        return undefined;
      }
      // Two frames: first paint is collapsed, second starts the open transition.
      let secondFrame = 0;
      const firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => setExpanded(true));
      });
      return () => {
        window.cancelAnimationFrame(firstFrame);
        window.cancelAnimationFrame(secondFrame);
      };
    }
    setExpanded(false);
    if (!mountedRef.current) return undefined;
    const timeout = window.setTimeout(() => {
      mountedRef.current = false;
      setMounted(false);
    }, motionMs);
    return () => window.clearTimeout(timeout);
  }, [open]);

  if (!mounted) return null;

  return (
    <S.Motion $open={expanded}>
      <S.MotionClip $open={expanded}>
        {open ? children : lastChildrenRef.current}
      </S.MotionClip>
    </S.Motion>
  );
};

export default LayoutAlertPresence;
